import React from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Badge, 
  LinearProgress,
  useTheme,
  useMediaQuery,
  Box
} from '@mui/material';
import { 
  CalendarToday, 
  PendingActions, 
  DoneAll, 
  Assignment 
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DashboardStatsProps } from './types';

/**
 * DashboardStats - Responsive component that displays statistical information about inspections
 */
const DashboardStats: React.FC<DashboardStatsProps> = ({ 
  todayCount, 
  pendingCount, 
  completedCount, 
  totalCount, 
  inspections 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate completion percentage for today's inspections
  const todayDateStr = format(new Date(), 'yyyy-MM-dd');
  const todayCompletedCount = inspections.filter(
    i => i.status === 'Pass' && i.inspectionDate === todayDateStr
  ).length;
  const todayCompletionPercentage = todayCount > 0 
    ? (todayCompletedCount / todayCount) * 100 
    : 0;

  // Responsive configuration
  const getResponsiveConfig = () => ({
    iconSize: isMobile ? 32 : isTablet ? 36 : 40,
    badgeSize: {
      fontSize: isMobile ? 12 : 14,
      height: isMobile ? 20 : 24,
      minWidth: isMobile ? 20 : 24
    },
    typography: {
      variant: isMobile ? 'subtitle2' : 'h6',
      fontSize: isMobile ? '0.875rem' : undefined
    },
    progressHeight: isMobile ? 6 : 8,
    cardPadding: isMobile ? 1.5 : 2
  });

  const config = getResponsiveConfig();

  // Stats data configuration
  const statsConfig = [
    {
      icon: CalendarToday,
      color: 'primary' as const,
      badgeColor: 'error' as const,
      progressColor: 'primary' as const,
      count: todayCount,
      label: 'Today',
      percentage: todayCompletionPercentage,
      description: isMobile ? undefined : `${todayCompletedCount}/${todayCount} completed`
    },
    {
      icon: PendingActions,
      color: 'info' as const,
      badgeColor: 'primary' as const,
      progressColor: 'info' as const,
      count: pendingCount,
      label: 'Pending',
      percentage: totalCount > 0 ? (pendingCount / totalCount) * 100 : 0,
      description: isMobile ? undefined : 'In progress + scheduled'
    },
    {
      icon: DoneAll,
      color: 'success' as const,
      badgeColor: 'success' as const,
      progressColor: 'success' as const,
      count: completedCount,
      label: 'Completed',
      percentage: totalCount > 0 ? (completedCount / totalCount) * 100 : 0,
      description: isMobile ? undefined : 'Passed inspections'
    },
    {
      icon: Assignment,
      color: 'action' as const,
      badgeColor: 'default' as const,
      progressColor: 'secondary' as const,
      count: totalCount,
      label: 'Total',
      percentage: 100,
      description: isMobile ? undefined : 'All inspections'
    }
  ];

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
      {statsConfig.map((stat) => {
        const IconComponent = stat.icon;
        
        return (
          <Grid 
            item 
            xs={6} 
            sm={6} 
            md={3} 
            key={stat.label}
          >
            <Card 
              elevation={isMobile ? 1 : 2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 3
                }
              }}
            >
              <CardContent 
                sx={{ 
                  textAlign: 'center', 
                  py: config.cardPadding,
                  px: { xs: 1, sm: 2 },
                  '&:last-child': { 
                    pb: config.cardPadding 
                  }
                }}
              >
                <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                  <Badge
                    badgeContent={stat.count}
                    color={stat.badgeColor}
                    sx={{ 
                      '& .MuiBadge-badge': {
                        fontSize: config.badgeSize.fontSize,
                        height: config.badgeSize.height,
                        minWidth: config.badgeSize.minWidth,
                        fontWeight: 600
                      }
                    }}
                  >
                    <IconComponent 
                      color={stat.color} 
                      sx={{ 
                        fontSize: config.iconSize,
                        opacity: 0.9
                      }} 
                    />
                  </Badge>
                </Box>

                <Typography 
                  variant={config.typography.variant as any}
                  sx={{ 
                    fontWeight: 600,
                    fontSize: config.typography.fontSize,
                    mb: { xs: 0.5, sm: 1 },
                    color: 'text.primary'
                  }}
                >
                  {stat.label}
                </Typography>

                {/* Optional description for larger screens */}
                {stat.description && !isMobile && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: 'block',
                      mb: 1,
                      fontSize: '0.75rem'
                    }}
                  >
                    {stat.description}
                  </Typography>
                )}

                <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={stat.percentage}
                    color={stat.progressColor}
                    sx={{ 
                      height: config.progressHeight,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 1
                      }
                    }}
                  />
                  
                  {/* Show percentage on mobile for better clarity */}
                  {isMobile && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.7rem'
                      }}
                    >
                      {Math.round(stat.percentage)}%
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default DashboardStats;